<script setup>
import { useLayouts } from '@/@layouts';
import { config } from '@/@layouts/config';
import { can } from '@/@layouts/plugins/casl';
import { getComputedNavLinkToProp, isNavLinkActive } from '@/@layouts/utils';
import { useNavItemVisibility } from '@/composables/useNavItemVisibility';
import { useAccountStore } from '@/stores/account.js';

const props = defineProps({
  item: {
    type: null,
    required: true,
  },
});

const { currentBusinessProfile } = storeToRefs(useAccountStore());

const { width: windowWidth } = useWindowSize();
const { isVerticalNavMini, dynamicI18nProps } = useLayouts();
const hideTitleAndBadge = isVerticalNavMini(windowWidth);
const { isEnvAllowed, isChannelAllowed, isDevOnly } = useNavItemVisibility();

const canAccessMenuItem = (item) => {
  const isAllowed = can(item.action, item.subject);

  if (item.isNeedBusinessProfile) {
    return isAllowed && currentBusinessProfile.value;
  }
  return isAllowed;
};
</script>

<template>
  <li
    v-if="canAccessMenuItem(item, item.action, item.subject) && isEnvAllowed(item) && isChannelAllowed(item)"
    class="nav-link"
    :class="{ disabled: item.disable, 'nav-item-dev-only': isDevOnly(item) }"
  >
    <Component
      :is="item.to ? 'RouterLink' : 'a'"
      v-bind="getComputedNavLinkToProp(item)"
      :class="{ 'router-link-active router-link-exact-active': isNavLinkActive(item, $router) }"
    >
      <Component
        :is="config.app.iconRenderer || 'div'"
        v-bind="item.icon || config.verticalNav.defaultNavItemIconProps"
        class="nav-item-icon"
      />
      <TransitionGroup name="transition-slide-x">
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-show="!hideTitleAndBadge"
          key="title"
          class="nav-item-title"
          v-bind="dynamicI18nProps(item.title, 'span')"
        >
          {{ item.title }}
        </Component>

        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-if="item.badgeContent"
          v-show="!hideTitleAndBadge"
          key="badge"
          class="nav-item-badge"
          :class="item.badgeClass"
          :title="item.badgeContent === 'B' ? 'Beta' : undefined"
          v-bind="dynamicI18nProps(item.badgeContent, 'span')"
        >
          {{ item.badgeContent }}
        </Component>
      </TransitionGroup>

      <div
        v-if="!hideTitleAndBadge && item.disable"
        key="lock"
        class="nav-group-lock-icon-wrap"
      >
        <VTooltip location="top center">
          <div class="tooltip-content">
            <p>{{ $t('Coming Soon') }}</p>
          </div>
          <template #activator="{ props }">
            <span v-bind="props">
              <Component
                :is="config.app.iconRenderer || 'div'"
                v-bind="config.icons.lock"
                class="nav-group-lock-icon"
              />
            </span>
          </template>
        </VTooltip>
      </div>
    </Component>
  </li>
</template>

<style lang="scss">
.layout-vertical-nav {
  .nav-link a {
    display: flex;
    align-items: center;
  }

  // Dev-only items (envOnly: 'development') get a tinted ICON so an unreleased page
  // reads as distinct from a shipped one at a glance. Groups tint the icon only; leaf
  // links also tint their title (rule below).
  // Unscoped on purpose: the rule serves both VerticalNavLink and VerticalNavGroup,
  // which both stamp .nav-item-dev-only. The active item is deliberately INCLUDED —
  // %nav-link-active paints a solid primary background, and the marker matters most on
  // the page you are actually looking at; `!important` is needed to win against it.
  .nav-item-dev-only {
    > a > .nav-item-icon,
    > .nav-group-label > .nav-item-icon {
      color: var(--wp-text-warning) !important;
    }
  }

  // Dev-only leaf links also tint their title. The active one is left alone: warning text
  // on the solid primary pill would be unreadable, and its icon still carries the marker.
  .nav-link.nav-item-dev-only > a:not(.router-link-exact-active) .nav-item-title {
    color: var(--wp-text-warning);
  }
}
</style>
